import {Injectable, Injector, OnDestroy, runInInjectionContext} from '@angular/core';
import {
  collection,
  CollectionReference,
  collectionData,
  doc,
  Firestore,
  updateDoc,
  writeBatch
} from '@angular/fire/firestore';
import {Ingredient} from './model/ingredient';
import {ListItem} from './model/list-item';
import {ItemCategory} from './model/item-category';
import {BehaviorSubject, Subject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IngredientService implements OnDestroy {

  ingredients: Ingredient[] = [];
  ingredients$: Subject<Ingredient[]> = new BehaviorSubject([]);

  private ingredientsCollection: CollectionReference<Ingredient>;

  constructor(private firestore: Firestore, private injector: Injector) {
    this.ingredientsCollection = collection(this.firestore, '/ingredients') as CollectionReference<Ingredient>;
    collectionData(this.ingredientsCollection, {idField: 'id'}).subscribe((ingredients: Ingredient[]) => {
      this.ingredients = ingredients.sort((a, b) => a.name.localeCompare(b.name));
      this.ingredients$.next(this.ingredients);
    });
  }

  find(ingredientName: string): Ingredient | undefined {
    const preparedIngredientName = (ingredientName || '').trim().toLowerCase();
    return this.ingredients.find(ingredient => ingredient.name.toLowerCase() === preparedIngredientName);
  }

  exists(ingredientName: string): boolean {
    return this.find(ingredientName) !== undefined;
  }

  updateCategory(ingredient: Ingredient, category: ItemCategory): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      updateDoc(doc(this.firestore, 'ingredients/' + ingredient.id), {
        type: category.name,
        new: false,
      })
    );
  }

  addNewIngredients(items: ListItem[]): Promise<void> {
    const newIngredients = items
      .filter(item => !this.exists(item.shortName))
      .map(item => {
        return {
          type: item.category.name,
          name: item.shortName,
          new: true,
        } as Ingredient;
      });

    return runInInjectionContext(this.injector, () => {
      const batch = writeBatch(this.firestore);
      newIngredients.forEach(ingredient => {
        const newDocRef = doc(this.ingredientsCollection);
        batch.set(newDocRef, ingredient);
      });
      return batch.commit();
    });
  }

  ngOnDestroy(): void {
    this.ingredients$.complete();
    this.ingredients$ = null;
  }
}
